<template>
  <div class="sync-settings-tab">
    <div class="tab-header mb-6">
      <h3 class="text-lg font-semibold text-gray-900">Sync Settings</h3>
      <p class="text-sm text-gray-600 mt-1">
        Configure performance, reliability, and monitoring options for synchronization
      </p>
    </div>

    <div class="form-sections space-y-6">
      <!-- Performance Settings -->
      <div class="form-section">
        <div class="section-header mb-4">
          <h4 class="text-md font-medium text-gray-800 flex items-center space-x-2">
            <i class="fas fa-tachometer-alt text-blue-500"></i>
            <span>Performance Settings</span>
          </h4>
          <p class="text-sm text-gray-600">
            Control how many records are processed at once
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="form-group">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Batch Size
            </label>
            <div class="relative">
              <input 
                type="number"
                v-model.number="localSettings.batch_size"
                class="form-input w-full"
                min="1"
                max="200"
                @change="emitUpdate"
                @input="validateBatchSize"
              >
              <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                <span class="text-gray-500 text-sm">records</span>
              </div>
            </div>
            <div class="mt-2 space-y-1">
              <p class="text-xs text-gray-600">
                Number of records to sync per batch (1-200)
              </p>
              <div v-if="batchSizeRecommendation" class="text-xs" :class="batchSizeRecommendation.class">
                <i :class="batchSizeRecommendation.icon" class="mr-1"></i>
                {{ batchSizeRecommendation.text }}
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Processing Priority
            </label>
            <select 
              v-model="localSettings.priority"
              class="form-select w-full"
              @change="emitUpdate"
            >
              <option value="low">Low Priority</option>
              <option value="normal">Normal Priority</option>
              <option value="high">High Priority</option>
            </select>
            <p class="text-xs text-gray-600 mt-2">
              Higher priority sync jobs will be processed first
            </p>
          </div>
        </div>
      </div>

      <!-- Reliability Settings -->
      <div class="form-section">
        <div class="section-header mb-4">
          <h4 class="text-md font-medium text-gray-800 flex items-center space-x-2">
            <i class="fas fa-shield-alt text-green-500"></i>
            <span>Reliability Settings</span>
          </h4>
          <p class="text-sm text-gray-600">
            Configure how failed syncs are handled
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="form-group">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Retry Attempts
            </label>
            <div class="relative">
              <input 
                type="number"
                v-model.number="localSettings.retry_attempts"
                class="form-input w-full"
                min="0"
                max="5"
                @change="emitUpdate"
              >
              <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                <span class="text-gray-500 text-sm">attempts</span>
              </div>
            </div>
            <p class="text-xs text-gray-600 mt-2">
              Number of times to retry failed syncs (0-5)
            </p>
          </div>

          <div class="form-group">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Retry Delay
            </label>
            <div class="relative">
              <input 
                type="number"
                v-model.number="localSettings.retry_delay"
                class="form-input w-full"
                min="10"
                max="300"
                step="10"
                @change="emitUpdate"
              >
              <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                <span class="text-gray-500 text-sm">seconds</span>
              </div>
            </div>
            <p class="text-xs text-gray-600 mt-2">
              Wait time between retry attempts (10-300 seconds)
            </p>
          </div>
        </div>

        <!-- Error Handling Strategy -->
        <div class="form-group mt-4">
          <label class="block text-sm font-medium text-gray-700 mb-3">
            Error Handling Strategy
          </label>
          <div class="space-y-3">
            <label class="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                   :class="{ 'border-blue-500 bg-blue-50': localSettings.error_strategy === 'skip_continue' }">
              <input 
                type="radio"
                value="skip_continue"
                v-model="localSettings.error_strategy"
                class="mt-1"
                @change="emitUpdate"
              >
              <div>
                <span class="font-medium text-gray-900">Skip and Continue</span>
                <p class="text-sm text-gray-600">
                  Skip failed records and continue processing the rest of the batch.
                </p>
              </div>
            </label>
            
            <label class="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                   :class="{ 'border-blue-500 bg-blue-50': localSettings.error_strategy === 'stop_on_error' }">
              <input 
                type="radio"
                value="stop_on_error"
                v-model="localSettings.error_strategy"
                class="mt-1"
                @change="emitUpdate"
              >
              <div>
                <span class="font-medium text-gray-900">Stop on Error</span>
                <p class="text-sm text-gray-600">
                  Stop processing the entire batch if any record fails.
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>

      <!-- Lead Creation Settings -->
      <div class="form-section">
        <div class="section-header mb-4">
          <h4 class="text-md font-medium text-gray-800 flex items-center space-x-2">
            <i class="fas fa-user-plus text-indigo-500"></i>
            <span>Lead Creation Workflow</span>
          </h4>
          <p class="text-sm text-gray-600">
            How campaigns are synced to Salesforce
          </p>
        </div>

        <!-- Important Notice -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div class="flex items-start space-x-3">
            <i class="fas fa-info-circle text-blue-600 mt-1"></i>
            <div class="text-sm">
              <p class="font-medium text-blue-900 mb-1">Simplified Lead Creation Workflow</p>
              <p class="text-blue-800">
                All campaigns are automatically synced as <strong>Leads</strong> in Salesforce. 
                Salesforce will handle the Lead-to-Opportunity conversion process internally if a Contact already exists. 
                This ensures proper tracking and attribution in your Salesforce workflow.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Monitoring Settings -->
      <div class="form-section">
        <div class="section-header mb-4">
          <h4 class="text-md font-medium text-gray-800 flex items-center space-x-2">
            <i class="fas fa-chart-line text-purple-500"></i>
            <span>Monitoring & Logging</span>
          </h4>
          <p class="text-sm text-gray-600">
            Configure what information is tracked and logged
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="form-group">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Log Level
            </label>
            <select 
              v-model="localSettings.log_level"
              class="form-select w-full"
              @change="emitUpdate"
            >
              <option value="error">Errors Only</option>
              <option value="warning">Warnings & Errors</option>
              <option value="info">Info, Warnings & Errors</option>
              <option value="debug">Debug (All Details)</option>
            </select>
            <div class="mt-2 text-xs text-gray-600">
              <div v-if="localSettings.log_level === 'error'">
                Only log sync failures and critical errors
              </div>
              <div v-else-if="localSettings.log_level === 'warning'">
                Log errors and potential issues (recommended)
              </div>
              <div v-else-if="localSettings.log_level === 'info'">
                Log successful syncs, warnings, and errors
              </div>
              <div v-else-if="localSettings.log_level === 'debug'">
                Log all sync details (use for troubleshooting)
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Log Retention
            </label>
            <select 
              v-model="localSettings.log_retention_days"
              class="form-select w-full"
              @change="emitUpdate"
            >
              <option value="7">7 days</option>
              <option value="30">30 days</option>
              <option value="90">90 days</option>
              <option value="365">1 year</option>
            </select>
            <p class="text-xs text-gray-600 mt-2">
              How long to keep sync logs before automatic cleanup
            </p>
          </div>
        </div>

        <!-- Notification Settings -->
        <div class="form-group mt-4">
          <h5 class="text-sm font-medium text-gray-700 mb-3">Notifications</h5>
          <div class="space-y-3">
            <label class="flex items-start space-x-3">
              <input 
                type="checkbox"
                v-model="localSettings.notify_on_failure"
                class="mt-1 rounded border-gray-300 text-blue-600"
                @change="emitUpdate"
              >
              <div>
                <span class="text-sm font-medium text-gray-700">Notify on Sync Failures</span>
                <p class="text-xs text-gray-500 mt-1">
                  Send email notifications when sync jobs fail repeatedly
                </p>
              </div>
            </label>

            <label class="flex items-start space-x-3">
              <input 
                type="checkbox"
                v-model="localSettings.notify_on_success"
                class="mt-1 rounded border-gray-300 text-blue-600"
                @change="emitUpdate"
              >
              <div>
                <span class="text-sm font-medium text-gray-700">Notify on Large Batch Success</span>
                <p class="text-xs text-gray-500 mt-1">
                  Send notifications when large batches (100+ records) sync successfully
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>

      <!-- Performance Impact Summary -->
      <div class="form-section">
        <div class="bg-gray-50 rounded-lg p-4">
          <h4 class="text-sm font-medium text-gray-800 mb-3">
            <i class="fas fa-info-circle text-gray-600 mr-2"></i>
            Performance Impact
          </h4>
          <div class="space-y-2 text-sm">
            <div class="flex items-center space-x-2">
              <div class="w-3 h-3 rounded-full" :class="getPerformanceColor()"></div>
              <span class="text-gray-700">{{ getPerformanceText() }}</span>
            </div>
            <div class="text-xs text-gray-600 ml-5">
              {{ getPerformanceDetails() }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';

export default {
  name: 'SyncSettingsTab',
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const localSettings = ref({
      batch_size: 50,
      priority: 'normal',
      retry_attempts: 3,
      retry_delay: 60,
      error_strategy: 'skip_continue',
      log_level: 'info',
      log_retention_days: 30,
      notify_on_failure: true,
      notify_on_success: false,
      // Duplicate Protection Settings
      enable_cross_object_search: false,
      type_mismatch_strategy: 'use_existing',
      cache_email_lookups: false,
      cache_ttl_hours: 24,
    });

    // Computed properties
    const batchSizeRecommendation = computed(() => {
      const size = localSettings.value.batch_size;
      if (size <= 10) {
        return {
          text: 'Very conservative - slowest but most reliable',
          class: 'text-green-600',
          icon: 'fas fa-check-circle'
        };
      } else if (size <= 50) {
        return {
          text: 'Recommended for most use cases',
          class: 'text-blue-600',
          icon: 'fas fa-thumbs-up'
        };
      } else if (size <= 100) {
        return {
          text: 'Good for high-volume syncing',
          class: 'text-yellow-600',
          icon: 'fas fa-exclamation-circle'
        };
      } else {
        return {
          text: 'Maximum performance - may hit API limits',
          class: 'text-red-600',
          icon: 'fas fa-exclamation-triangle'
        };
      }
    });

    // Methods
    const validateBatchSize = () => {
      if (localSettings.value.batch_size < 1) {
        localSettings.value.batch_size = 1;
      } else if (localSettings.value.batch_size > 200) {
        localSettings.value.batch_size = 200;
      }
    };

    const getPerformanceColor = () => {
      const size = localSettings.value.batch_size;
      const retries = localSettings.value.retry_attempts;
      
      if (size <= 25 && retries >= 3) return 'bg-green-500';
      if (size <= 100 && retries >= 2) return 'bg-yellow-500';
      return 'bg-red-500';
    };

    const getPerformanceText = () => {
      const size = localSettings.value.batch_size;
      const retries = localSettings.value.retry_attempts;
      
      if (size <= 25 && retries >= 3) return 'Conservative - High Reliability';
      if (size <= 100 && retries >= 2) return 'Balanced - Good Performance & Reliability';
      return 'Aggressive - High Performance, Lower Reliability';
    };

    const getPerformanceDetails = () => {
      const size = localSettings.value.batch_size;
      
      if (size <= 25) {
        return 'Small batches reduce API load and improve error isolation.';
      } else if (size <= 100) {
        return 'Medium batches balance sync speed with API limits.';
      } else {
        return 'Large batches maximize throughput but may hit Salesforce API limits.';
      }
    };

    const emitUpdate = () => {
      emit('update:modelValue', { ...localSettings.value });
    };

    // Cooldown Recommendation
    const cooldownRecommendation = computed(() => {
      const hours = localSettings.value.lead_cooldown_hours || 0;
      
      if (hours === 0) {
        return {
          text: 'No cooldown - All campaigns will create new Leads',
          class: 'text-blue-600',
          icon: 'fas fa-info-circle'
        };
      } else if (hours <= 24) {
        return {
          text: 'Recommended setting for most workflows',
          class: 'text-green-600',
          icon: 'fas fa-check-circle'
        };
      } else if (hours <= 168) { // 1 week
        return {
          text: 'Conservative cooldown - May skip some valid campaigns',
          class: 'text-yellow-600',
          icon: 'fas fa-exclamation-circle'
        };
      } else {
        return {
          text: 'Very long cooldown - Consider reducing for better tracking',
          class: 'text-red-600',
          icon: 'fas fa-exclamation-triangle'
        };
      }
    });

    // Initialize with prop values
    onMounted(() => {
      localSettings.value = {
        batch_size: props.modelValue.batch_size || 50,
        priority: props.modelValue.priority || 'normal',
        retry_attempts: props.modelValue.retry_attempts ?? 3,
        retry_delay: props.modelValue.retry_delay || 60,
        error_strategy: props.modelValue.error_strategy || 'skip_continue',
        log_level: props.modelValue.log_level || 'info',
        log_retention_days: props.modelValue.log_retention_days || 30,
        notify_on_failure: props.modelValue.notify_on_failure ?? true,
        notify_on_success: props.modelValue.notify_on_success || false,
        // Lead Creation Settings
        lead_cooldown_hours: props.modelValue.lead_cooldown_hours || 0,
      };
    });

    // Watch for external prop changes
    watch(() => props.modelValue, (newValue) => {
      localSettings.value = {
        batch_size: newValue.batch_size || 50,
        priority: newValue.priority || 'normal',
        retry_attempts: newValue.retry_attempts ?? 3,
        retry_delay: newValue.retry_delay || 60,
        error_strategy: newValue.error_strategy || 'skip_continue',
        log_level: newValue.log_level || 'info',
        log_retention_days: newValue.log_retention_days || 30,
        notify_on_failure: newValue.notify_on_failure ?? true,
        notify_on_success: newValue.notify_on_success || false,
        // Lead Creation Settings
        lead_cooldown_hours: newValue.lead_cooldown_hours || 0,
      };
    }, { deep: true });

    return {
      localSettings,
      batchSizeRecommendation,
      cooldownRecommendation,
      validateBatchSize,
      getPerformanceColor,
      getPerformanceText,
      getPerformanceDetails,
      emitUpdate,
    };
  },
};
</script>

<style scoped>
.form-select,
.form-input {
  @apply block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500;
}

.form-section {
  @apply border-b border-gray-100 pb-6 last:border-b-0 last:pb-0;
}

.tab-header {
  @apply border-b border-gray-200 pb-4;
}

input[type="radio"]:checked {
  @apply text-blue-600;
}

@media (max-width: 768px) {
  .grid-cols-1.md\\:grid-cols-2 {
    @apply grid-cols-1;
  }
  
  .form-sections {
    @apply space-y-4;
  }
  
  .form-section {
    @apply pb-4;
  }
}
</style>